<template>
  <ak-modal v-model:active="isModalActive" size="big">
    <ak-modal-body>
      <ak-modal-title>{{ $t('Clone an automation') }}</ak-modal-title>
      <div
        v-if="loading"
        class="one flex flex--center flex--justify-center min-h-300"
      >
        <template v-if="loading">
          <ak-loading-spinner />
        </template>
      </div>
      <ak-form v-if="!loading">
        <ak-field
          v-if="userLevelAbove('developer')"
          :label="$t('Agency to clone to')"
          required
        >
          <ak-select
            v-model="selectedAgency"
            name="selectAgency"
            :options="agencies"
            :allow-empty="false"
            label="label"
            :max-height="150"
            @select="fetchAccounts"
          />
        </ak-field>
        <ak-field
          v-if="userLevelAbove('developer')"
          :label="$t('Account to clone to')"
          required
        >
          <ak-select
            v-model="selectedAccount"
            name="selectAccount"
            :options="accounts"
            :allow-empty="false"
            :max-height="150"
            label="label"
          />
        </ak-field>
        <ak-field :label="$t('Automation Name')" required>
          <ak-input
            id="cloneName"
            ref="nameInput"
            v-model="clone.name"
            required
          />
        </ak-field>
        <ak-field :label="$t('Folder')" required>
          <ak-select
            id="cloneFolder"
            v-model="clone.folder"
            name="cloneFolder"
            :options="folders"
            :allow-empty="false"
            :max-height="150"
          />
        </ak-field>
      </ak-form>
    </ak-modal-body>
    <ak-modal-footer>
      <ak-modal-button id="cloneBtn" @click.stop.prevent="cloneAutomation">
        {{ $t('Clone') }}
      </ak-modal-button>
    </ak-modal-footer>
  </ak-modal>
</template>

<script>
import { defineComponent, ref, watch } from 'vue'
import Modal from '@akerolabs/akero-vue/src/components/modal/Modal.vue'
import ModalBody from '@akerolabs/akero-vue/src/components/modal/ModalBody.vue'
import ModalTitle from '@akerolabs/akero-vue/src/components/modal/ModalTitle.vue'
import ModalFooter from '@akerolabs/akero-vue/src/components/modal/ModalFooter.vue'
import ModalButton from '@akerolabs/akero-vue/src/components/modal/ModalButton.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import { userLevelIsHigerThan } from '@akerolabs/akero-vue/src/services/users'
import LoadingSpinner from '@akerolabs/akero-vue/src/components/loading/LoadingSpinner.vue'
import { success, error } from '../utils'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { sleep } from '@akerolabs/akero-vue/src/utils/helpers'
import { formatDate } from '@akerolabs/akero-vue/src/filters/date'

import { useRouter } from '../../../legacy/router'

export default defineComponent({
  name: 'CloneAutomationModal',
  components: {
    [Modal.name]: Modal,
    [ModalBody.name]: ModalBody,
    [ModalTitle.name]: ModalTitle,
    [ModalFooter.name]: ModalFooter,
    [ModalButton.name]: ModalButton,
    [Form.name]: Form,
    [Field.name]: Field,
    [Input.name]: Input,
    [Select.name]: Select,
    [LoadingSpinner.name]: LoadingSpinner,
  },
  props: {
    /**
     * @type {Boolean}
     * @description Used to display and hide the modal
     */
    isActive: {
      type: Boolean,
      default: false,
    },
    /**
     * @type {Object}
     * @description Automation data to clone from
     */
    data: {
      type: Object,
      default: () => ({}),
    },
    /**
     * @type {String}
     * @description Current active agency id
     */
    agencyId: {
      type: String,
      default: undefined,
    },
    /**
     * @type {String}
     * @description Current active account id
     */
    accountId: {
      type: String,
      default: undefined,
    },
    /**
     * @type {Object}
     * @description Current active user
     */
    user: {
      type: Object,
      default: undefined,
    },
  },
  emits: ['update:isActive', 'hasProcessed'],
  setup(props, { emit }) {
    const router = useRouter()

    const isModalActive = ref(false)
    const agencies = ref([])
    const accounts = ref([])
    const folders = ref([])
    const loading = ref(true)
    const selectedAgency = ref({})
    const selectedAccount = ref({})
    const selectedFolder = ref('none')
    const clone = ref({
      account: props.accountId,
      agency: props.agencyId,
      folder: 'none',
      name: props.data.name,
      latestCatchhook: null,
    })
    const nameInput = ref(null)

    /**
     * @description Emits the update to the isActive prop and inits/resets the data when modal is opened/closed
     */
    watch(isModalActive, async (value) => {
      emit('update:isActive', value)
      if (value) {
        loading.value = true
        if (userLevelIsHigerThan('developer')) {
          await fetchAgencies()
          await fetchAccounts(false)
        }
        await fetchFolders(false)
        clone.value = {
          name: `${props.data.name} Clone ${formatDate(
            Date.now(),
            'DD/MM/YYYY HH:mm',
          )}`,
          account: props.accountId,
          agency: props.agencyId,
          folder: 'none',
        }
        loading.value = false
      } else {
        agencies.value = []
        accounts.value = []
        selectedAgency.value = {}
        selectedAccount.value = {}
        clone.value.name = ''
      }
    })

    /**
     * @description Updates the internal isModalActive based on the isActive prop
     */
    watch(
      () => props.isActive,
      async (value) => {
        isModalActive.value = value
      },
    )

    /**
     * @description Update the clone to agency data when an agency is selected
     */
    watch(
      selectedAgency,
      (value) => {
        clone.value.agency = value.value
      },
      { deep: true },
    )

    /**
     * @description Update the clone to account data when an account is selected
     */
    watch(
      selectedAccount,
      (value) => {
        clone.value.account = value.value
      },
      { deep: true },
    )

    /**
     * @description Fetch all the agencies
     */
    const fetchAgencies = async () => {
      try {
        const { data } = await request.get('/agencies', {
          params: {
            overrideAccess: true,
            deleted: JSON.stringify({ $ne: true }),
            per_page: 0,
          },
        })
        const selectData = data.data.map((item) => {
          return {
            value: item._id,
            label: item.name,
          }
        })
        agencies.value = selectData
        const defaultAgency = selectData.find((i) => {
          return i.value === props.agencyId
        })
        selectedAgency.value = defaultAgency
      } catch (ex) {
        error('Failed to fetch agencies', ex)
      }
    }

    /**
     * @param {Boolean} showLoad Do you want the fetch to affect the loading state of the modal
     * @description Fetch the accounts for an agency
     */
    const fetchAccounts = async (showLoad = true) => {
      await sleep(50)
      if (showLoad) {
        loading.value = true
      }
      try {
        const { data } = await request.get('/accounts', {
          params: {
            agency: selectedAgency.value.value,
            overrideAccess: true,
            deleted: JSON.stringify({ $ne: true }),
            per_page: 0,
          },
        })
        const selectData = data.data.map((item) => {
          return {
            value: item._id,
            label: item.name,
          }
        })
        let defaultAccount = selectData.find((i) => {
          return i.value === props.accountId
        })
        if (!defaultAccount) {
          defaultAccount = selectData[0]
        }
        accounts.value = selectData
        selectedAccount.value = defaultAccount
        clone.value.folder = 'none'
      } catch (ex) {
        error('Failed to fetch accounts', ex)
      }
      if (showLoad) {
        loading.value = false
      }
    }

    /**
     * @param {Boolean} showLoad Do you want the fetch to affect the loading state of the modal
     * @description Fetch the available folders to clone to
     */
    const fetchFolders = async (showLoad = true) => {
      await sleep(50)
      const selectData = ['none']
      if (showLoad) {
        loading.value = true
      }
      try {
        const account = selectedAccount.value.value
          ? selectedAccount.value.value
          : props.accountId
        const agency = selectedAgency.value.value
          ? selectedAgency.value.value
          : props.agencyId
        const { data } = await request.get(`/accounts/${account}`, {
          params: {
            agency,
            overrideAccess: true,
            filters: 'settings.workflow.folders',
          },
        })
        data.data.settings.workflow.folders.forEach((folder) => {
          selectData.push(folder)
        })
        folders.value = selectData
      } catch (ex) {
        error('Failed to fetch folders', ex)
      }

      if (showLoad) {
        loading.value = false
      }
    }

    /**
     * @description Make api request to clone automation and goto the edit screen
     */
    const cloneAutomation = async () => {
      const isValid = await nameInput.value.validate(true)
      if (isValid) {
        loading.value = true
        try {
          const cloned = await request.post(
            `/tasks/${props.data._id}/clone`,
            clone.value,
          )
          const { data } = await request.get(`/accounts/${clone.value.account}`)
          isModalActive.value = false
          success('Automation cloned')
          router.push('automationEdit', {
            id: cloned.data.data._id,
            agencyId: props.agencyId,
            accountSlug: data.data.slug,
          })
        } catch (ex) {
          isModalActive.value = false
          error('Failed to clone automation', ex)
        }
        loading.value = false
      }
    }

    return {
      isModalActive,
      loading,
      userLevelAbove: userLevelIsHigerThan,
      agencies,
      selectedAgency,
      accounts,
      selectedAccount,
      fetchAccounts,
      clone,
      folders,
      selectedFolder,
      cloneAutomation,
      nameInput,
    }
  },
})
</script>

<i18n>
{
  "en": {
    "Clone an automation": "Clone an automation",
    "Agency to clone to": "Agency to clone to",
    "Account to clone to": "Account to clone to",
    "Automation Name": "Automation Name",
    "Folder": "Folder",
    "Clone": "Clone"
  },
  "en-US": {
    "Clone an automation": "Clone an automation",
    "Agency to clone to": "Agency to clone to",
    "Account to clone to": "Account to clone to",
    "Automation Name": "Automation Name",
    "Folder": "Folder",
    "Clone": "Clone"
  }
}
</i18n>
